// src/pages/admin/Page_EducaFiles/Page_EducaFiles.js
//
// M09 - Pagina admin del "mini Drive" sobre el bucket
// gs://educa-files-edu360. Layout 2 columnas: sidebar de carpetas a la
// izquierda + grid de ficheros a la derecha. Wizard de subida (Fase 6)
// se monta como modal al pulsar "Subir".

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { PageHeader, EducaThemeProvider } from "../../../ui";
import { EducaFilesAPI } from "../../../api";
import { useAuth } from "../../../hooks";
import {
  EducaFolderSidebar,
  EducaFilesToolbar,
  EducaFileGrid,
  EducaFileDetailDrawer,
  EducaFileUploadWizard,
} from "../../../components/Admin/EducaFilesCmp/_shared";
import styles from "./Page_EducaFiles.module.scss";

const api = new EducaFilesAPI();

const ROOT_KEY = "__root__"; // valor interno para la carpeta raiz EducaFiles
const PAGE_SIZE = 24;

export function Page_EducaFiles() {
  const { accessToken } = useAuth();

  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(ROOT_KEY);
  const [files, setFiles] = useState([]);
  const [filters, setFilters] = useState({ kind: "", visibility: "", q: "" });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const [editing, setEditing] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  // Carga inicial de carpetas (y al recargar).
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!accessToken) return;
      try {
        setLoadingFolders(true);
        const data = await api.listFolders(accessToken);
        if (alive) setFolders(data.folders || []);
      } catch (e) {
        if (alive) {
          message.error(e?.body?.message || "Error cargando carpetas");
        }
      } finally {
        if (alive) setLoadingFolders(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken, reloadTick]);

  // Reset de pagina al cambiar carpeta o filtros.
  useEffect(() => {
    setPage(1);
  }, [activeFolder, filters.kind, filters.visibility, filters.q]);

  // Carga de ficheros segun carpeta/filtros/pagina.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!accessToken) return;
      try {
        setLoadingFiles(true);
        const query = {
          folder: activeFolder === ROOT_KEY ? "" : activeFolder,
          kind: filters.kind || undefined,
          visibility: filters.visibility || undefined,
          q: filters.q || undefined,
          page,
          limit: PAGE_SIZE,
        };
        const data = await api.listFiles(accessToken, query);
        if (alive) {
          setFiles(data.items || []);
          setTotal(data.total || 0);
        }
      } catch (e) {
        if (alive) {
          message.error(e?.body?.message || "Error cargando ficheros");
        }
      } finally {
        if (alive) setLoadingFiles(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [
    accessToken,
    activeFolder,
    filters.kind,
    filters.visibility,
    filters.q,
    page,
    reloadTick,
  ]);

  const headerSubtitle = useMemo(() => {
    if (activeFolder === ROOT_KEY) return "EducaFiles (raiz)";
    return `EducaFiles / ${activeFolder}`;
  }, [activeFolder]);

  const buildStreamUrl = useCallback((id) => api.streamUrl(id), []);

  const handleCreateFolder = useCallback(
    async (name) => {
      await api.createFolder(accessToken, name);
      message.success(`Carpeta "${name}" creada`);
      reload();
    },
    [accessToken, reload],
  );

  const handleDeleteFolder = useCallback(
    async (name) => {
      try {
        await api.deleteFolder(accessToken, name);
        message.success("Carpeta borrada");
        if (activeFolder === name) setActiveFolder(ROOT_KEY);
        reload();
      } catch (e) {
        message.error(e?.body?.message || "Error borrando carpeta");
      }
    },
    [accessToken, activeFolder, reload],
  );

  const handleDeleteFile = useCallback(
    async (file) => {
      try {
        await api.deleteFile(accessToken, file.id);
        message.success("Fichero borrado");
        setEditing(null);
        reload();
      } catch (e) {
        message.error(e?.body?.message || "Error borrando fichero");
      }
    },
    [accessToken, reload],
  );

  const handleSaveFile = useCallback(
    async (id, patch) => {
      await api.updateFile(accessToken, id, patch);
      reload();
    },
    [accessToken, reload],
  );

  const handlePreview = useCallback((file) => {
    const url = api.streamUrl(file.id);
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  // Descarga via signed URL temporal (GET firmado contra GCS con
  // Content-Disposition: attachment). Funciona igual para ficheros
  // publicos y privados; el browser baja el blob directo de GCS y
  // dispara la descarga real (no preview).
  const handleDownload = useCallback(
    async (file) => {
      if (!file?.id) return;
      const hide = message.loading("Preparando descarga...", 0);
      try {
        const { url, filename } = await api.getDownloadUrl(
          accessToken,
          file.id,
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = filename || file.originalName || file.name || "";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (e) {
        message.error(e?.body?.message || "Error generando descarga");
      } finally {
        hide();
      }
    },
    [accessToken],
  );

  const handleUpload = useCallback(() => {
    setUploadOpen(true);
  }, []);

  const handleUploaded = useCallback(
    (uploadedFile) => {
      // Si subimos a una carpeta distinta a la activa, cambia el foco para
      // que el usuario vea el resultado donde quedo.
      if (uploadedFile?.folder) {
        setActiveFolder(uploadedFile.folder);
      } else {
        setActiveFolder(ROOT_KEY);
      }
      reload();
    },
    [reload],
  );

  return (
    <EducaThemeProvider>
      <div className={styles.pageEducaFiles}>
        <PageHeader title="EducaFiles" subtitle={headerSubtitle} />

        <div className={styles.layout}>
          <aside className={styles.sidebar}>
            <EducaFolderSidebar
              folders={folders}
              activeFolder={activeFolder}
              onSelect={setActiveFolder}
              loading={loadingFolders}
              rootKey={ROOT_KEY}
              onCreate={handleCreateFolder}
              onDelete={handleDeleteFolder}
            />
          </aside>

          <main className={styles.main}>
            <EducaFilesToolbar
              filters={filters}
              onFiltersChange={setFilters}
              onUpload={handleUpload}
            />
            <EducaFileGrid
              files={files}
              total={total}
              loading={loadingFiles}
              page={page}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              buildStreamUrl={buildStreamUrl}
              onEdit={setEditing}
              onDelete={handleDeleteFile}
              onPreview={handlePreview}
              onDownload={handleDownload}
            />
          </main>
        </div>

        <EducaFileDetailDrawer
          open={!!editing}
          file={editing}
          folders={folders}
          streamUrl={editing ? api.streamUrl(editing.id) : ""}
          onClose={() => setEditing(null)}
          onSave={handleSaveFile}
          onDelete={handleDeleteFile}
          onDownload={handleDownload}
        />

        <EducaFileUploadWizard
          open={uploadOpen}
          folders={folders}
          defaultFolder={activeFolder}
          accessToken={accessToken}
          api={api}
          onClose={() => setUploadOpen(false)}
          onUploaded={handleUploaded}
        />
      </div>
    </EducaThemeProvider>
  );
}
